namespace $ {

	export type $shm_hitalama_process_status =
		| 'running'
		| 'done'
		| 'failed'
		| 'superseded'

	export type $shm_hitalama_process_data< TResult = unknown > = {
		status?: $shm_hitalama_process_status
		started_at?: $mol_time_moment
		finished_at?: $mol_time_moment
		fiber?: $mol_wire_fiber< any, any, TResult >
		runs_count?: number
		promise?: PromiseLike< TResult >
		result?: TResult
		error?: unknown
	}

	export type $shm_hitalama_process_task< TResult > =
		( process: $shm_hitalama_process< TResult > ) => PromiseLike< TResult >

	export class $shm_hitalama_process<
		TResult = unknown,
	> extends $mol_object {

		static current?: $shm_hitalama_process< any >
		static result_store_enabled = true
		private static calls_all_current = [] as $shm_hitalama_process< any >[]
		private static calls_by_name = new Map< string, $shm_hitalama_process< any >[] >
		private static parent_key_by_name = new Map< string, string >

		static method_name( method: Function ) {
			return ( method as { orig?: Function } ).orig?.name || method.name.trim()
		}

		static method_key( host: object, method: Function ) {
			return `${ host.toString() }.${ this.method_name( method ) }`
		}

		@ $mol_mem_key
		static calls_by_key( key: string ) {
			return this.calls_by_name.get( key ) ?? []
		}

		@ $mol_mem
		static all() {
			return this.calls_all_current
		}

		@ $mol_mem_key
		static children( parent: $shm_hitalama_process< any > ) {
			return this.children_raw( parent )
		}

		private static children_raw( parent: $shm_hitalama_process< any > ) {
			return this.calls_all_current.filter( process => process.parent === parent )
		}

		@ $mol_mem
		static roots() {
			const all = this.all()
			const known = new Set( all )

			return all.filter( process => !process.parent || !known.has( process.parent ) )
		}

		@ $mol_mem
		static keys() {
			return [ ... this.calls_by_name.keys() ]
		}

		@ $mol_mem_key
		static parent_key( key: string ) {
			return this.parent_key_by_name.get( key ) ?? ''
		}

		private static all_atom() {
			const method = this.all as typeof $shm_hitalama_process.all & {
				orig: typeof $shm_hitalama_process.all
			}

			return $mol_wire_atom.solo( this, method.orig )
		}

		private static keys_atom() {
			const method = this.keys as typeof $shm_hitalama_process.keys & {
				orig: typeof $shm_hitalama_process.keys
			}

			return $mol_wire_atom.solo( this, method.orig )
		}

		private static calls_atom( key: string ) {
			const method = this.calls_by_key as typeof $shm_hitalama_process.calls_by_key & {
				orig: typeof $shm_hitalama_process.calls_by_key
			}

			return $mol_wire_atom.plex( this, method.orig, key )
		}

		private static parent_key_atom( key: string ) {
			const method = this.parent_key as typeof $shm_hitalama_process.parent_key & {
				orig: typeof $shm_hitalama_process.parent_key
			}

			return $mol_wire_atom.plex( this, method.orig, key )
		}

		static calls( host: object, method: Function ) {
			const key = this.method_key( host, method )
			return this.calls_by_key( key )
		}

		static calls_named( host: object, method_name: string ) {
			return this.calls_by_key( `${ host.toString() }.${ method_name }` )
		}

		@ $mol_mem_key
		static last_by_key( key: string ) {
			const calls = this.calls_by_key( key )

			return calls[ calls.length - 1 ]
		}

		static last( host: object, method: Function ) {
			return this.last_by_key( this.method_key( host, method ) )
		}

		static last_named( host: object, method_name: string ) {
			return this.last_by_key( `${ host.toString() }.${ method_name }` )
		}

		static atom< Result >( host: object, method: Function ) {
			const field = `${ this.method_name( method ) }()`
			return Object.getOwnPropertyDescriptor( host, field )?.value as
				| $mol_wire_atom< object, readonly unknown[], Result >
				| undefined
		}

		static result< Result >( host: object, method: Function ) {
			return this.atom< Result >( host, method )?.result() as Result
		}

		static register( host: object, method: Function, process: $shm_hitalama_process< any > ) {
			const key = this.method_key( host, method )
			const calls = this.calls_by_name.get( key ) ?? []
			const next = [ ... calls, process ]

			this.calls_all_current = [ ... this.calls_all_current, process ]
			this.calls_by_name.set( key, next )
			this.all_atom().absorb()
			if( calls.length === 0 ) this.keys_atom().absorb()
			this.calls_atom( key ).absorb()
		}

		static touch( host: object, method: Function ) {
			const key = this.method_key( host, method )
			const parent = this.current
			const parent_key = parent?.host ? `${ parent.host }.${ parent.method_name }` : ''
			const prev = this.parent_key_by_name.get( key ) ?? ''
			if( prev === parent_key ) return

			this.parent_key_by_name.set( key, parent_key )
			this.parent_key_atom( key ).absorb()
		}

		// Reliable for $shm_hitalama_process_wire_action where current is restored per fiber run.
		// Plain async actions can lose this context after await if another process starts.
		parent = $shm_hitalama_process.current

		constructor(
			private readonly task: $shm_hitalama_process_task< TResult >,
			public host?: object,
			public method_name = '',
			public args: readonly unknown[] = [],
		) { super() }

		private data_current = {} as $shm_hitalama_process_data< TResult >

		@ $mol_mem
		data( next?: $shm_hitalama_process_data< TResult > ) {
			if( next ) this.data_current = next
			return this.data_current
		}

		private data_atom() {
			const method = this.data as $shm_hitalama_process< TResult >['data'] & {
				orig: $shm_hitalama_process< TResult >['data']
			}

			return $mol_wire_atom.solo( this, method.orig )
		}

		data_update( next: Partial< $shm_hitalama_process_data< TResult > > ) {
			this.data_current = {
				... this.data_current,
				... next,
			}
			this.data_atom().absorb()
		}

		data_status() {
			return this.data_current.status
		}

		run_count_inc() {
			this.data_update({
				runs_count: ( this.data_current.runs_count ?? 0 ) + 1,
			})
		}

		state() {
			const data = this.data()

			if( data.status === undefined ) return undefined
			if( data.status === 'running' && data.promise ) $mol_fail_hidden( data.promise )
			if( data.status === 'failed' ) $mol_fail_hidden( data.error )
			if( data.status === 'done' ) return data.result

			return undefined
		}

		supersede() {
			if( this.data_current.status !== 'running' ) return

			this.data_update({
				status: 'superseded',
				finished_at: new $mol_time_moment,
			})

			for( const child of $shm_hitalama_process.children_raw( this ) ) {
				child.supersede()
			}
		}

		duration() {
			const data = this.data()

			if( !data.started_at ) return 0

			return ( data.finished_at ?? new $mol_time_moment ).valueOf() - data.started_at.valueOf()
		}

		start() {
			this.data_update({
				status: 'running',
				started_at: new $mol_time_moment,
				finished_at: undefined,
				promise: undefined,
				runs_count: 0,
				result: undefined,
				error: undefined,
			})

			const prev = $shm_hitalama_process.current
			const promise = ( async()=> {
				$shm_hitalama_process.current = this

				try {
					const result = await this.task( this )
					this.data_update({
						...( $shm_hitalama_process.result_store_enabled ? { result } : {} ),
						status: 'done',
					})
					return result
				} catch( error ) {
					this.data_update({
						error,
						status: 'failed',
					})
					throw error
				} finally {
					this.data_update({
						finished_at: new $mol_time_moment,
					})
					$shm_hitalama_process.current = prev
				}
			})()

			this.data_update({ promise })
			return promise
		}

	}

	export function $shm_hitalama_process_async_action< Host extends object >(
		host: Host,
		field: PropertyKey,
		descr?: TypedPropertyDescriptor< ( ... args: any[] )=> any >,
	): void {
		const descr2 = descr ?? Reflect.getOwnPropertyDescriptor( host, field ) as unknown as TypedPropertyDescriptor< ( ... args: any[] )=> any >
		const orig = descr2.value!

		const value = function( this: Host, ... args: any[] ) {
			const process = new $shm_hitalama_process(
				()=> orig.apply( this, args ),
				this,
				String( field ),
				args,
			)

			$shm_hitalama_process.register( this, value, process )

			return process.start()
		}

		Object.defineProperty( value, 'name', { value: orig.name } )
		Object.assign( value, { orig } )

		descr2.value = value
		Reflect.defineProperty( host, field, descr2 )
	}

}
